import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ChevronRight, ArrowLeft, ArrowRight, Menu, Printer, Award } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import StepCard from '../components/StepCard';
import { modules, getModuleIcon } from '../data/modules';
import { Progress } from '../components/ui/progress';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { printCertificate } from '../lib/certificate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TRAINING_BLUE = '#2563EB';

export default function ModulePage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  const staticModule = modules.find(m => m.id === slug);
  const [dbModule, setDbModule] = useState(null);
  // Use the rich static module if it exists; otherwise fall back to whatever
  // /api/modules/{slug} returned (covers PDF-imported and admin-created modules).
  const module = staticModule || dbModule;
  const currentIndex = modules.findIndex(m => m.id === slug);
  const prevModule = currentIndex > 0 ? modules[currentIndex - 1] : null;
  const nextModule = currentIndex >= 0 && currentIndex < modules.length - 1 ? modules[currentIndex + 1] : null;

  useEffect(() => {
    const stepParam = searchParams.get('step');
    setActiveStep(stepParam !== null ? parseInt(stepParam, 10) : 0);
    window.scrollTo(0, 0);
  }, [slug, searchParams]);

  // For DB-only (PDF-imported / admin-created) modules — fetch the full
  // module payload so we can render its steps.
  useEffect(() => {
    if (staticModule || !slug) return;
    axios.get(`${API}/modules/${slug}`)
      .then(r => setDbModule(r.data))
      .catch(() => setDbModule(null));
  }, [slug, staticModule]);

  useEffect(() => {
    axios.get(`${API}/training/progress`, { withCredentials: true }).then(r => {
      const done = new Set(r.data.filter(p => p.module_id === slug).map(p => p.step_index));
      setCompletedSteps(done);
    }).catch(() => {});
  }, [slug]);

  const handleMarkComplete = async (stepIndex) => {
    try {
      await axios.post(`${API}/training/complete?module_id=${slug}&step_index=${stepIndex}`, {}, { withCredentials: true });
      setCompletedSteps(prev => new Set([...prev, stepIndex]));
    } catch (e) { console.error('Failed to mark complete'); }
  };

  const handleDownloadCertificate = async () => {
    try {
      const r = await axios.get(`${API}/training/certificate/${slug}`, { withCredentials: true });
      printCertificate(r.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Certificate not available yet');
    }
  };

  if (!module) {
    return (
      <div className="min-h-screen bg-background" data-testid="module-not-found">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <h2 className="font-['Outfit'] text-2xl font-semibold text-foreground mb-2">Module Not Found</h2>
            <p className="text-sm text-muted-foreground mb-6">The module you're looking for doesn't exist.</p>
            <Link to="/" className="text-sm font-medium text-[#42CDD7] hover:text-[#42CDD7]" data-testid="back-home-btn">
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const Icon = getModuleIcon(module.icon);
  const completedCount = completedSteps.size;
  const progress = (completedCount / module.steps.length) * 100;

  return (
    <div className="min-h-screen bg-background" data-testid="module-page">
      <Header />

      <div className="flex">
        {/* Desktop Sidebar */}
        <Sidebar currentModule={slug} />

        {/* Mobile Sidebar */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="p-0 w-72">
            <div className="p-4 border-b border-border">
              <p className="font-['Outfit'] text-sm font-semibold text-foreground">Training Modules</p>
            </div>
            <nav className="p-3 space-y-0.5">
              {modules.map((mod) => {
                const ModIcon = getModuleIcon(mod.icon);
                return (
                  <Link
                    key={mod.id}
                    to={`/module/${mod.id}`}
                    onClick={() => setSidebarOpen(false)}
                    data-testid={`mobile-sidebar-${mod.id}`}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
                      mod.id === slug ? 'bg-accent text-[#42CDD7] font-medium' : 'text-foreground/80 hover:bg-muted'
                    }`}
                  >
                    <ModIcon className="w-4 h-4" strokeWidth={1.5} />
                    <span>{mod.title}</span>
                  </Link>
                );
              })}
            </nav>
          </SheetContent>
        </Sheet>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Mobile sidebar toggle */}
            <button
              data-testid="mobile-sidebar-toggle"
              className="lg:hidden flex items-center gap-2 text-sm text-foreground/80 mb-4 hover:text-[#42CDD7] transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-4 h-4" /> Browse Modules
            </button>

            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs mb-6" data-testid="breadcrumb">
              <Link to="/" className="text-muted-foreground hover:text-[#42CDD7] transition-colors">Home</Link>
              <ChevronRight className="w-3 h-3 text-muted-foreground" />
              <span className="text-[#42CDD7] font-medium">{module.title}</span>
            </nav>

            {/* Module Header */}
            <div className="mb-8" data-testid="module-header">
              <div className="flex items-start gap-4 mb-4">
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${module.color}12` }}
                >
                  <Icon className="w-7 h-7" style={{ color: module.color }} strokeWidth={1.5} />
                </div>
                <div>
                  <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground" data-testid="module-title">
                    {module.title}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">{module.subtitle}</p>
                </div>
              </div>
              <p className="text-sm text-foreground/80 leading-relaxed">{module.description}</p>
              <div className="flex items-center gap-2 mt-4">
                <Link
                  to={`/quick-ref/${slug}`}
                  data-testid="print-all-btn"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-foreground/80 bg-muted hover:bg-accent hover:text-[#42CDD7] border border-border hover:border-[#42CDD7]/30 rounded-lg transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" /> Print All Reference Cards
                </Link>
                <Link
                  to={`/quick-ref/${slug}?step=${activeStep}`}
                  data-testid="print-step-btn"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-[#42CDD7] bg-accent hover:bg-accent border border-[#42CDD7]/30 rounded-lg transition-colors"
                >
                  <Printer className="w-3.5 h-3.5" /> Print Current Step
                </Link>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-8 bg-muted rounded-xl p-4 border border-border" data-testid="progress-section">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {completedCount} of {module.steps.length} steps completed
                </span>
                <span className="text-xs font-semibold" style={{ color: TRAINING_BLUE }}>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2 bg-accent [&>div]:bg-[#2563EB]" data-testid="progress-bar" />
            </div>

            {/* Certificate banner (shows when 100% complete) */}
            {progress === 100 && (
              <div
                className="mb-8 rounded-xl p-5 border flex items-center gap-4"
                style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(66,205,215,0.10) 100%)', borderColor: 'rgba(245,158,11,0.35)' }}
                data-testid="certificate-banner"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(245,158,11,0.18)' }}>
                  <Award className="w-6 h-6" style={{ color: '#F59E0B' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">Module complete — certificate ready</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Download your i3 Radiology completion certificate for CPD and audit records.</p>
                </div>
                <button
                  onClick={handleDownloadCertificate}
                  data-testid="download-certificate-btn"
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-transform hover:translate-y-[-1px]"
                  style={{ backgroundColor: '#F59E0B' }}
                >
                  <Award className="w-4 h-4" /> Download certificate
                </button>
              </div>
            )}

            {/* Steps Table of Contents */}
            <div className="mb-8 bg-background border border-border rounded-xl p-5" data-testid="toc">
              <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-3">In This Module</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {module.steps.map((step, idx) => (
                  <button
                    key={idx}
                    data-testid={`toc-step-${idx}`}
                    onClick={() => setActiveStep(idx)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                      idx === activeStep
                        ? 'bg-accent text-[#42CDD7] font-medium'
                        : 'text-foreground/80 hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                      idx === activeStep ? 'bg-[#42CDD7] text-white' : 'bg-muted text-muted-foreground'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="truncate">{step.title}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Active Step Content */}
            <div className="space-y-3" data-testid="steps-content">
              {module.steps.map((step, idx) => (
                <StepCard
                  key={idx}
                  step={step}
                  stepNumber={idx + 1}
                  totalSteps={module.steps.length}
                  isActive={idx === activeStep}
                  isCompleted={completedSteps.has(idx)}
                  onClick={() => setActiveStep(idx === activeStep ? idx : idx)}
                  onMarkComplete={() => handleMarkComplete(idx)}
                />
              ))}
            </div>

            {/* Step Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-border" data-testid="step-nav">
              <button
                data-testid="prev-step-btn"
                disabled={activeStep === 0}
                onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-foreground/80 hover:text-[#42CDD7] disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-lg hover:bg-accent"
              >
                <ArrowLeft className="w-4 h-4" /> Previous Step
              </button>
              <button
                data-testid="next-step-btn"
                disabled={activeStep === module.steps.length - 1}
                onClick={() => setActiveStep(Math.min(module.steps.length - 1, activeStep + 1))}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-[#42CDD7] hover:text-[#42CDD7] disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-lg hover:bg-accent"
              >
                Next Step <ArrowRight className="w-4 h-4" />
              </button>
            </div>

            {/* Module Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8 pt-6 border-t border-border" data-testid="module-nav">
              {prevModule ? (
                <Link
                  to={`/module/${prevModule.id}`}
                  data-testid="prev-module-link"
                  className="flex items-center gap-3 p-4 border border-border rounded-xl hover:border-[#42CDD7]/30 hover:bg-accent/30 transition-all group"
                >
                  <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-pink-500" />
                  <div>
                    <p className="text-xs text-muted-foreground">Previous Module</p>
                    <p className="text-sm font-semibold text-foreground group-hover:text-[#42CDD7]">{prevModule.title}</p>
                  </div>
                </Link>
              ) : <div />}
              {nextModule && (
                <Link
                  to={`/module/${nextModule.id}`}
                  data-testid="next-module-link"
                  className="flex items-center justify-end gap-3 p-4 border border-border rounded-xl hover:border-[#42CDD7]/30 hover:bg-accent/30 transition-all group text-right"
                >
                  <div>
                    <p className="text-xs text-muted-foreground">Next Module</p>
                    <p className="text-sm font-semibold text-foreground group-hover:text-[#42CDD7]">{nextModule.title}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-pink-500" />
                </Link>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
